import React from 'react';
import { Link } from 'react-router';
import SEOHead from '../../components/seo/seo-head';
import { SEO_CONFIG, SITE_URL } from '../../constants/seo-config';
import AppRoutes from '../../routes/routes';
import AxonIcon from '../../components/icons/axon-icon';
import axonHero from '../../../images/axon-ai-neurology-assistant.webp';

import './marketing-axon.scss';

const FAQS = [
  {
    question: 'What is Axon?',
    answer: 'Axon is the AI neurology assistant from Neurochecklists. It helps users ask clinical neurology questions in natural language and returns structured answers grounded in the referenced Neurochecklists knowledge base.'
  },
  {
    question: 'Who is Axon designed for?',
    answer: 'Axon is designed for neurologists, doctors in training, medical students, researchers, and healthcare professionals who need a faster way to navigate clinical neurology information.'
  },
  {
    question: 'Where does Axon get its information?',
    answer: 'Axon retrieves relevant material from the Neurochecklists knowledge base, which organises neurology criteria, guidelines, protocols, and references into practical clinical checklists.'
  },
  {
    question: 'Can Axon replace clinical judgement?',
    answer: 'No. Axon is an information and clinical decision-support tool. It does not replace professional judgement, local guidance, direct source review, or an individual patient assessment.'
  },
  {
    question: 'How do I start using Axon?',
    answer: 'Create or sign in to a Neurochecklists account, open Axon, and enter a clinical neurology question. You can then review the structured response and its supporting references.'
  }
];

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebApplication',
      '@id': `${SITE_URL}/axon#application`,
      name: 'Axon',
      alternateName: 'Axon AI Neurology Assistant',
      url: `${SITE_URL}/axon`,
      applicationCategory: 'MedicalApplication',
      operatingSystem: 'Web',
      description: SEO_CONFIG.axon.description,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Neurochecklists',
        url: SITE_URL
      },
      audience: {
        '@type': 'MedicalAudience',
        audienceType: 'Clinicians, medical students, researchers and healthcare professionals'
      }
    },
    {
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/axon#faq`,
      mainEntity: FAQS.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Neurochecklists',
          item: SITE_URL
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Axon AI Neurology Assistant',
          item: `${SITE_URL}/axon`
        }
      ]
    }
  ]
};

export default class MarketingAxon extends React.Component {
  constructor() {
    super();
    this.observer = null;
  }

  componentDidMount() {
    const reveals = document.querySelectorAll('.axon-page .axon-reveal');

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.14 });
      reveals.forEach(element => this.observer.observe(element));
    } else {
      reveals.forEach(element => element.classList.add('is-visible'));
    }
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  render() {
    return (
      <main className="axon-page">
        <SEOHead {...SEO_CONFIG.axon} structuredData={structuredData} />

        <section className="axon-hero" aria-labelledby="axon-title">
          <div className="axon-hero-copy axon-reveal is-visible">
            <div className="axon-eyebrow">
              <AxonIcon size={20} color="currentColor" />
              <span>From Neurochecklists</span>
            </div>
            <h1 id="axon-title">Axon, your AI neurology assistant</h1>
            <p>Ask clinical neurology questions and receive structured answers grounded in the referenced Neurochecklists knowledge base.</p>
            <div className="axon-actions">
              <Link className="axon-button axon-button-primary" to={AppRoutes.chat}>Ask Axon</Link>
              <Link className="axon-button axon-button-secondary" to={AppRoutes.explore}>Explore evidence</Link>
            </div>
          </div>
          <figure className="axon-hero-visual axon-reveal is-visible">
            <img
              src={axonHero}
              alt="Illustration of a brain and axon connecting a clinical question with referenced neurology knowledge"
              width="1536"
              height="1024"
            />
          </figure>
        </section>

        <section className="axon-definition axon-section" aria-labelledby="what-is-axon">
          <div className="axon-section-intro axon-reveal">
            <h2 id="what-is-axon">What is Axon?</h2>
            <p>Axon is the AI neurology assistant built into Neurochecklists. It lets clinicians, researchers, and learners ask questions in natural language, then returns concise, structured answers grounded in an extensive, referenced neurology knowledge base. Supporting sources remain visible so users can verify the evidence behind each response.</p>
          </div>
          <div className="axon-definition-points axon-reveal">
            <div>
              <strong>Grounded</strong>
              <span>Answers are drawn from Neurochecklists content rather than the open web.</span>
            </div>
            <div>
              <strong>Structured</strong>
              <span>Complex criteria and protocols are organised into practical responses.</span>
            </div>
            <div>
              <strong>Traceable</strong>
              <span>References help you review the source behind the answer.</span>
            </div>
          </div>
        </section>

        <section className="axon-workflow axon-section" aria-labelledby="how-axon-works">
          <div className="axon-section-intro axon-reveal">
            <h2 id="how-axon-works">How does Axon work?</h2>
            <p>Axon turns a natural-language question into a focused search of the Neurochecklists knowledge base, then organises the relevant findings into a readable response.</p>
          </div>
          <ol className="axon-workflow-list">
            <li className="axon-reveal">
              <div>
                <h3>Ask a clinical question</h3>
                <p>Use everyday clinical language. Ask about diagnostic criteria, investigations, differentials, or management principles.</p>
              </div>
            </li>
            <li className="axon-reveal">
              <div>
                <h3>Retrieve relevant evidence</h3>
                <p>Axon identifies the most relevant material inside the structured Neurochecklists neurology database.</p>
              </div>
            </li>
            <li className="axon-reveal">
              <div>
                <h3>Review the answer and sources</h3>
                <p>Read the organised response, then inspect the supporting references before applying the information.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="axon-use-cases axon-section" aria-labelledby="axon-use-cases">
          <div className="axon-section-intro axon-reveal">
            <h2 id="axon-use-cases">One assistant, several clinical contexts</h2>
            <p>Axon supports the moments when the right neurology source is known to exist, but finding and structuring it takes too long.</p>
          </div>
          <div className="axon-use-grid">
            <article className="axon-use-primary axon-reveal">
              <AxonIcon size={44} color="currentColor" />
              <h3>At the point of care</h3>
              <p>Revisit criteria, investigation pathways, and management considerations while preparing for a clinical discussion.</p>
            </article>
            <article className="axon-reveal">
              <h3>During research</h3>
              <p>Find a useful starting point and move quickly from a question to the relevant source material.</p>
            </article>
            <article className="axon-use-tinted axon-reveal">
              <h3>While learning</h3>
              <p>Turn broad neurology topics into clear, structured explanations for revision and teaching preparation.</p>
            </article>
            <article className="axon-reveal">
              <h3>Before presenting</h3>
              <p>Check key definitions, classifications, and guidelines before a meeting, tutorial, or case review.</p>
            </article>
          </div>
        </section>

        <section className="axon-safety axon-section" aria-labelledby="responsible-use">
          <div className="axon-safety-panel axon-reveal">
            <div className="axon-safety-label">Responsible use</div>
            <h2 id="responsible-use">Built to support judgement, not replace it</h2>
            <p>Axon provides educational information and clinical decision support. It does not diagnose patients or replace professional judgement, local protocols, direct source review, or an individual clinical assessment. Always verify important decisions against the cited source and current guidance.</p>
          </div>
        </section>

        <section className="axon-faq axon-section" aria-labelledby="axon-faq-title">
          <div className="axon-section-intro axon-reveal">
            <h2 id="axon-faq-title">Questions about Axon</h2>
            <p>Clear answers about what Axon does, where its information comes from, and how to use it responsibly.</p>
          </div>
          <div className="axon-faq-list axon-reveal">
            {FAQS.map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{item.question}<span aria-hidden="true">+</span></summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="axon-final-cta axon-section" aria-labelledby="axon-final-title">
          <div className="axon-reveal">
            <AxonIcon size={42} color="currentColor" />
            <h2 id="axon-final-title">Put a neurology question to Axon</h2>
            <p>Start with the question already on your mind.</p>
            <Link className="axon-button axon-button-primary" to={AppRoutes.chat}>Ask Axon</Link>
          </div>
        </section>
      </main>
    );
  }
}
